import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DashboardHeader } from "@/components/DashboardHeader";
import { StudentView } from "@/components/StudentView";
import { PerformanceView } from "@/components/PerformanceView";
import { LiveTopscorer } from "@/components/LiveTopscorer";
import SubTabs from "@/components/SubTabs";
import TabPageLayout from "@/components/TabPageLayout";
import { TimerView } from "@/components/TimerView";
import { ForecastView } from "@/components/ForecastView";
import { StudentGoalView } from "@/components/StudentGoalView";
import { ElevOkonomiView } from "@/components/ElevOkonomiView";
import { KaraktererTab } from "@/components/KaraktererTab";
import HubView, { ElevHubView } from "@/components/HubView";
import { UndervisningView } from "@/pages/UndervisningPage";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/i18n";
import { Menu, ChevronDown } from "lucide-react";

// Mobil tab-menu: hamburger dropdown på mobil, normal TabsList på desktop
function MobileTabMenu({ tabs, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const activeTab = tabs.find(t => t.id === value) || tabs[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <>
      {/* Mobil: dropdown */}
      <div className="relative md:hidden" ref={ref}>
        <button onClick={() => setOpen(v => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary border border-border text-sm font-semibold">
          <Menu className="w-4 h-4 text-muted-foreground" />
          <span>{activeTab?.label}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-2 bg-background border border-border rounded-2xl shadow-xl overflow-hidden z-50 min-w-[180px]">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => { onChange(tab.id); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors text-left
                  ${tab.id === value ? "bg-[#FF2A6D] text-white" : "text-foreground hover:bg-secondary"}`}>
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop: normal TabsList */}
      <TabsList className="hidden md:flex h-12 bg-secondary border border-border p-1 rounded-full">
        {tabs.map(tab => (
          <TabsTrigger key={tab.id} value={tab.id}
            className="rounded-full px-3 sm:px-5 py-2 text-[13px] sm:text-sm font-semibold data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out"
            data-testid={tab.testId}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opfølgning #35 (#3): venlig fallback på Score-fanen i stedet for blank side
// når scoreboard-data endnu ikke er hentet (eller fejlede).
function ScoreboardLoading() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
      <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm">Indlæser scoreboard…</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, token, logout, hasRettighed } = useAuth();
  const isGuest = user?.role === "guest";
  const isAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);
  const isMember = user?.role === "member";
  // #38.6: Light-medarbejder (vikar/læge). Ser Hub, Undervisning, Score — men
  // IKKE Omsætning(staff)/Forecast/KPI/Timer-dashboard.
  const isLight = user?.role === "light";
  // "Fuld" medarbejder/admin: ser ledelses-faner. Light ekskluderet.
  const isFuldStab = isAdmin || isMember;
  const canSeeStaff = isAdmin || isMember || isLight; // bruger Tabs-UI (ikke guest-grenen)
  // #48.3: medarbejdere i Administration/TNBC må vælge by (alle skoler) som admin.
  const _afd = (user?.primaer_afdeling || user?.city || "").toLowerCase();
  const erAdminEllerTNBC = _afd.includes("administration") || _afd === "tnbc";
  const kanVaelgeBy = isAdmin || (isMember && erAdminEllerTNBC);

  const [searchParams, setSearchParams] = useSearchParams();

  const canSeeHub = isAdmin || isMember || isLight;
  const VALID_PERIODS = ["day", "week", "month", "last_month", "prev_month"];
  const defaultView = canSeeHub ? "hub" : "staff";
  // #45: "Omsætning"(staff) + "KPI"(kpi) slået sammen til én "performance"-fane.
  // Bagudkompatibelt: gamle ?tab=staff / ?tab=kpi URLs mappes til "performance".
  const VALID_TABS = canSeeStaff
    ? ["hub", ...(isFuldStab ? ["performance"] : []), "student", ...(isFuldStab ? ["timer"] : []), "undervisning", ...(isFuldStab ? ["forecast"] : [])]
    : ["elevhub", "student", "timer", "maal", "undervisning", "okonomi"];
  // #45: map gamle fane-navne til den nye samlede "performance"-fane.
  const _rawTabFromUrl = searchParams.get("tab");
  const tabFromUrl = (_rawTabFromUrl === "staff" || _rawTabFromUrl === "kpi") ? "performance" : _rawTabFromUrl;
  const periodFromUrl = searchParams.get("period");
  const studentPeriodFromUrl = searchParams.get("studentPeriod");

  const defaultGuestView = "elevhub";
  const activeView = canSeeStaff
    ? (VALID_TABS.includes(tabFromUrl) ? tabFromUrl : defaultView)
    : (VALID_TABS.includes(tabFromUrl) ? tabFromUrl : defaultGuestView);
  // #48.6: Performance lander altid på "Dag" som første visning (ikke gemt mellem besøg).
  const period = VALID_PERIODS.includes(periodFromUrl) ? periodFromUrl : "day";
  const studentPeriod = VALID_PERIODS.includes(studentPeriodFromUrl) ? studentPeriodFromUrl : "week";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => next.set(k, v));
      return next;
    }, { replace: true });
  };

  const handleTabChange = (tab) => updateParams({ tab });
  const setPeriod = (p) => updateParams({ period: p });
  const setStudentPeriod = (p) => updateParams({ studentPeriod: p });

  const [staffData, setStaffData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [managerData, setManagerData] = useState(null);
  const [hubActiveTab, setHubActiveTab] = useState("overview");
  const [elevHubActiveTab, setElevHubActiveTab] = useState("overview");
  const [timerData, setTimerData] = useState(null);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerProgress, setTimerProgress] = useState(null);
  const [timerError, setTimerError] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();
  const [impersonatedStudent, setImpersonatedStudent] = useState(
    location.state?.impersonatedStudent || null
  ); // { name, token }
  const ADMIN_CITIES = ["Alle", "Koebenhavn", "Aarhus", "Odense", "Aalborg", "Kolding"];
  const CITY_LABELS = { Alle: "Alle skoler", Koebenhavn: "København", Aarhus: "Aarhus", Odense: "Odense", Aalborg: "Aalborg", Kolding: "Kolding" };
  const [adminKpiCity, setAdminKpiCity] = useState("Alle");

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchTimerData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    setTimerLoading(true);
    setTimerError(null);
    setTimerProgress(null);
    const pollInterval = setInterval(async () => {
      try {
        const p = await axios.get(`${API}/dashboard/timer/progress`, { headers });
        if (p.data.active) setTimerProgress(p.data);
      } catch {}
    }, 1000);
    try {
      const res = await axios.get(`${API}/dashboard/timer`, { headers, timeout: 180000 });
      setTimerData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Ukendt fejl";
      setTimerError(msg);
    } finally {
      clearInterval(pollInterval);
      setTimerLoading(false);
      setTimerProgress(null);
    }
  };

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const requests = [
        axios.get(`${API}/dashboard/student?period=${studentPeriod}`, { headers: authHeaders }),
      ];

      // Only fetch staff data if user has access. #38.6: light henter IKKE staff
      // (Omsætning er skjult for light) — kun student/Score.
      if (isFuldStab) {
        requests.unshift(
          axios.get(`${API}/dashboard/staff?period=${period}`, { headers: authHeaders })
        );
      }
      // #48.3: almindelig medarbejder → KPI låst til egen afdeling (ingen by-param).
      // Administration/TNBC-medarbejder bruger by-vælger som admin (håndteres nedenfor).
      if (isMember && !kanVaelgeBy) {
        requests.push(axios.get(`${API}/dashboard/manager`, { headers: authHeaders }));
      }
      if (kanVaelgeBy) {
        if (adminKpiCity === "Alle") {
          // Fetch all cities for aggregation
          const allCities = ["Koebenhavn", "Aarhus", "Odense", "Aalborg", "Kolding"];
          requests.push(
            Promise.all(allCities.map(c => axios.get(`${API}/dashboard/manager?city=${c}`, { headers: authHeaders }).catch(() => null)))
              .then(results => {
                const valid = results.filter(r => r?.data).map(r => r.data);
                if (valid.length === 0) return null;
                // Aggregate across cities
                const base = { ...valid[0] };
                base.city = "Alle";
                base.total_6month = valid.reduce((s, d) => s + (d.total_6month || 0), 0);
                base.goal_6month = valid.reduce((s, d) => s + (d.goal_6month || 0), 0);
                base.monthly_goal = valid.reduce((s, d) => s + (d.monthly_goal || 0), 0);
                base.monthly_revenue = valid[0].monthly_revenue.map((_, i) =>
                  valid.reduce((s, d) => s + (d.monthly_revenue?.[i] || 0), 0)
                );
                base.behandling_revenue = valid.reduce((s, d) => s + (d.behandling_revenue || 0), 0);
                base.behandling_count = valid.reduce((s, d) => s + (d.behandling_count || 0), 0);
                base.student_count = valid.reduce((s, d) => s + (d.student_count || 0), 0);
                base.num_receipts = valid.reduce((s, d) => s + (d.num_receipts || 0), 0);
                base.avg_per_receipt = base.num_receipts > 0 ? Math.round(base.behandling_revenue / base.num_receipts) : 0;
                // Weighted averages
                const withBooking = valid.filter(d => d.booking_avg_period != null);
                base.booking_avg_period = withBooking.length > 0
                  ? Math.round(withBooking.reduce((s, d) => s + d.booking_avg_period, 0) / withBooking.length * 10) / 10
                  : null;
                base.booking_pct = withBooking.length > 0
                  ? Math.round(withBooking.reduce((s, d) => s + (d.booking_pct || 0), 0) / withBooking.length * 10) / 10
                  : null;
                const withSat = valid.filter(d => d.satisfaction_score != null);
                base.satisfaction_score = withSat.length > 0
                  ? Math.round(withSat.reduce((s, d) => s + d.satisfaction_score, 0) / withSat.length * 10) / 10
                  : null;
                base.satisfaction_count = valid.reduce((s, d) => s + (d.satisfaction_count || 0), 0);
                const withFrav = valid.filter(d => d.avg_fravær_pct != null);
                base.avg_fravær_pct = withFrav.length > 0
                  ? Math.round(withFrav.reduce((s, d) => s + d.avg_fravær_pct, 0) / withFrav.length * 10) / 10
                  : null;
                // Vægtet gennemsnit af kr/time per by (vægtet på behandlingsomsætning)
                const rphCities = valid.filter(d => d.revenue_per_hour > 0 && d.behandling_revenue > 0);
                base.revenue_per_hour = rphCities.length > 0
                  ? Math.round(rphCities.reduce((s, d) => s + d.revenue_per_hour * d.behandling_revenue, 0) / rphCities.reduce((s, d) => s + d.behandling_revenue, 0))
                  : null;
                // Monthly KPI scores — average across cities
                base.monthly_kpi_scores = valid[0].monthly_kpi_scores?.map((_, i) => {
                  const scores = valid.map(d => d.monthly_kpi_scores?.[i]).filter(s => s != null && s > 0);
                  return scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
                }) || [];
                base.prev_booking_avg = null;
                base.prev_satisfaction_score = null;
                base.break_even_6month = valid.reduce((s, d) => s + (d.break_even_6month || 0), 0) || undefined;
                return { data: base };
              })
          );
        } else {
          requests.push(axios.get(`${API}/dashboard/manager?city=${adminKpiCity}`, { headers: authHeaders }));
        }
      }

      const settled = await Promise.allSettled(requests);
      const results = settled.map((r) => (r.status === "fulfilled" ? r.value : null));

      // Check for 401 in any result
      for (const s of settled) {
        if (s.status === "rejected" && s.reason?.response?.status === 401) {
          logout();
          return;
        }
      }

      if (isFuldStab && (isMember || isAdmin)) {
        if (results[0]) setStaffData(results[0].data);
        if (results[1]) setStudentData(results[1].data);
        if (results[2]) setManagerData(results[2].data);
      } else {
        // Light + guest: ingen staff-request → results[0] er student.
        if (results[0]) setStudentData(results[0].data);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, studentPeriod, canSeeStaff, token, adminKpiCity, kanVaelgeBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchForecastData = async () => {
    setForecastLoading(true);
    setForecastError(null);
    try {
      const res = await axios.get(`${API}/dashboard/forecast`, { headers: authHeaders });
      setForecastData(res.data);
    } catch (err) {
      setForecastError(err.response?.data?.detail || err.message || "Ukendt fejl");
    } finally {
      setForecastLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === "timer" && !timerData && !timerLoading) {
      fetchTimerData();
    }
    if (activeView === "forecast" && !forecastData && !forecastLoading) {
      fetchForecastData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  const TimerLoadingScreen = () => (
    <div className="flex flex-col items-center justify-center py-24 gap-6">
      {timerError ? (
        <>
          <p className="text-sm text-red-400">Fejl: {timerError}</p>
          <button
            onClick={fetchTimerData}
            className="px-4 py-2 rounded-full text-[13px] font-semibold bg-secondary text-foreground hover:bg-secondary/80"
          >
            Prøv igen
          </button>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-foreground">
            {timerProgress?.phase || "Henter timerdata fra Podio..."}
          </p>
          <div className="w-72 bg-secondary rounded-full h-2.5 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#FF2A6D] transition-all duration-500"
              style={{ width: `${timerProgress?.pct ?? 0}%` }}
            />
          </div>
          <p className="text-[13px] text-muted-foreground">
            {timerProgress?.pct != null ? `${timerProgress.pct}%` : ""}
            {timerProgress?.done && timerProgress?.total ? ` — ${timerProgress.done} / ${timerProgress.total}` : ""}
          </p>
        </>
      )}
    </div>
  );

  const lang = user?.lang || "da";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">{t(lang, "loadingDashboard")}</p>
        </div>
      </div>
    );
  }

  const displayCity = (city) => city === "Koebenhavn" ? "København" : city;
  const cityLabel = user?.city ? ` ${displayCity(user.city)}` : "";

  const PERIOD_OPTIONS = [
    { key: "day", label: t(lang, "periodDay") },
    { key: "week", label: t(lang, "periodWeek") },
    { key: "month", label: t(lang, "periodMonth") },
    { key: "last_month", label: t(lang, "periodLastMonth") },
    { key: "prev_month", label: t(lang, "periodPrevMonth") },
  ];

  // Periode-vælger = den fælles SubTabs-skabelon (ét rettested for stilen).
  const PeriodFilter = ({ value, onChange }) => (
    <SubTabs tabs={PERIOD_OPTIONS} value={value} onChange={onChange} />
  );

  if (impersonatedStudent) {
    return (
      <ImpersonatedView
        impToken={impersonatedStudent.token}
        adminToken={token}
        studentName={impersonatedStudent.name}
        adminUser={user}
        onExit={() => setImpersonatedStudent(null)}
        lang={lang}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-container">
      <DashboardHeader
        user={user}
        onLogout={logout}
      />

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {canSeeStaff ? (
          <Tabs value={activeView} onValueChange={handleTabChange} className="w-full">
            {/* Dashboard-overskrift fjernet (Opf. #33) — rolle/by fremgår af top-nav + badge */}
            <div className="mt-4 flex items-center justify-between mb-3 flex-wrap gap-3">
              {/* Hovedmenu (venstre) */}
              <div className="flex items-center gap-2 min-w-0">
                <MobileTabMenu
                  value={activeView}
                  onChange={handleTabChange}
                  tabs={[
                    ...(canSeeHub ? [{ id: "hub", label: "Hub" }] : []),
                    ...(isFuldStab ? [{ id: "performance", label: "Performance", testId: "performance-tab" }] : []),
                    ...(isFuldStab ? [{ id: "forecast", label: "Forecast" }] : []),
                    { id: "student", label: t(lang, "tabStudent"), testId: "student-tab" },
                    ...(isFuldStab ? [{ id: "timer", label: "Elever" }] : []),
                    { id: "undervisning", label: "Undervisning" },
                  ]}
                />
              </div>

              {/* Live Topscorer: øverst til højre i menu-rækken (alle faner). */}
              {isFuldStab && (
                <div className="ml-auto order-2">
                  <LiveTopscorer token={token} />
                </div>
              )}

              {/* Hub-undermenu: på mobil egen fuld-bredde række her; på desktop
                  vises den inde i HubView (samme Forecast-template). Performance/
                  Score-periode-faner renderes nu INDE i deres view (som Forecast). */}
              {activeView === "hub" && canSeeHub && (
                <div className="w-full md:hidden order-3">
                  <HubView.MobileMenu activeTab={hubActiveTab} onChange={setHubActiveTab} />
                </div>
              )}
            </div>

            {canSeeHub && (
              <TabsContent value="hub" className="mt-0">
                <HubView token={token} currentUser={user?.name} userCity={user?.city} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} isMember={isMember} isLight={isLight} activeTab={hubActiveTab} onTabChange={setHubActiveTab} />
              </TabsContent>
            )}

            {isFuldStab && (
              <TabsContent value="performance" className="mt-0">
                <TabPageLayout submenu={{ tabs: PERIOD_OPTIONS, value: period, onChange: setPeriod }}>
                  <PerformanceView
                    token={token}
                    staffData={staffData}
                    managerData={managerData}
                    period={period}
                    adminKpiCity={adminKpiCity}
                    setAdminKpiCity={setAdminKpiCity}
                    adminCities={ADMIN_CITIES}
                    cityLabels={CITY_LABELS}
                    kanVaelgeBy={kanVaelgeBy}
                    isAdmin={isAdmin}
                    lang={lang}
                    refreshing={refreshing}
                  />
                </TabPageLayout>
              </TabsContent>
            )}
            <TabsContent value="student" className="mt-0">
              <TabPageLayout submenu={{ tabs: PERIOD_OPTIONS, value: studentPeriod, onChange: setStudentPeriod }}>
                {studentData
                  ? <StudentView data={studentData} period={studentPeriod} refreshing={refreshing} lang={lang} />
                  : <ScoreboardLoading />}
              </TabPageLayout>
            </TabsContent>
            {(isAdmin || isMember) && (
              <TabsContent value="timer" className="mt-0">
                {timerData ? (
                  <TimerView data={timerData} userCity={user?.city} isAdmin={isAdmin} canImpersonate={isAdmin || hasRettighed("kan_se_elever")} token={token} impersonatedStudent={impersonatedStudent} setImpersonatedStudent={setImpersonatedStudent} />
                ) : (
                  <TimerLoadingScreen />
                )}
              </TabsContent>
            )}
            {(isFuldStab || isLight) && (
              <TabsContent value="undervisning" className="mt-0">
                <UndervisningView embedded />
              </TabsContent>
            )}
            {(isAdmin || isMember) && (
              <TabsContent value="forecast" className="mt-0">
                {forecastData ? (
                  <ForecastView data={forecastData} />
                ) : forecastLoading ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4">
                    <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-muted-foreground">Henter forecast fra Podio...</p>
                  </div>
                ) : forecastError ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4">
                    <p className="text-sm text-red-400">Fejl: {forecastError}</p>
                    <button onClick={fetchForecastData}
                      className="px-4 py-2 rounded-full text-[13px] font-semibold bg-secondary text-foreground hover:bg-secondary/80">
                      Prøv igen
                    </button>
                  </div>
                ) : null}
              </TabsContent>
            )}
          </Tabs>
        ) : (
          /* Guest: Elev + Timer tabs */
          <Tabs value={activeView} onValueChange={handleTabChange} className="w-full">
            {/* Elev-dashboard-overskrift fjernet (Opf. #33) */}
            <div className="mt-4 mb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <MobileTabMenu
                  value={activeView}
                  onChange={handleTabChange}
                  tabs={[
                    { id: "elevhub", label: "Elev Hub" },
                    { id: "student", label: "Score" },
                    { id: "timer", label: "Elever" },
                    { id: "maal", label: "Mål" },
                    { id: "undervisning", label: "Undervisning" },
                    { id: "okonomi", label: "Stamdata" },
                  ]}
                />
                {activeView === "student" && (
                  <PeriodFilter value={studentPeriod} onChange={setStudentPeriod} testId="guest-period-filter" />
                )}
              </div>
              {activeView === "elevhub" && (
                <div className="mt-2">
                  <ElevHubView.MobileMenu activeTab={elevHubActiveTab} onChange={setElevHubActiveTab} />
                </div>
              )}
            </div>
            <TabsContent value="elevhub" className="mt-0">
              <ElevHubView token={token} currentUser={user?.name} userCity={user?.city} isAdmin={false} isSuperAdmin={false} isMember={false} activeTab={elevHubActiveTab} onTabChange={setElevHubActiveTab} />
            </TabsContent>
            <TabsContent value="student" className="mt-0">
              {studentData
                ? <StudentView data={studentData} period={studentPeriod} refreshing={refreshing} lang={lang} />
                : <ScoreboardLoading />}
            </TabsContent>
            <TabsContent value="timer" className="mt-0">
              {timerData ? (
                <TimerView data={timerData} userCity={user?.city} isAdmin={false} isGuest={user?.role === "guest"} />
              ) : (timerLoading || timerError) ? (
                <TimerLoadingScreen />
              ) : null}
            </TabsContent>
            <TabsContent value="maal" className="mt-0">
              <StudentGoalView token={token} />
            </TabsContent>
            <TabsContent value="undervisning" className="mt-0">
              {/* Elev: UndervisningView slår selv egen retning op via /dashboard/student/kpi
                  og fastlåser den. Tab-bar (Kosmetolog/Frisør) skjules automatisk for guest. */}
              <UndervisningView embedded />
            </TabsContent>
            <TabsContent value="okonomi" className="mt-0">
              <ElevOkonomiView token={token} />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

function ImpersonatedView({ impToken, adminToken, studentName, adminUser, onExit, lang }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const authHeaders = { Authorization: `Bearer ${impToken}` };

  // Privacy-niveau: kun superadmin/owner ser fortrolige tabs (Kontrakter, Betaling, Noter)
  // og felter (CPR i klartekst, helbred, forældre). kan_se_elever får read-only stamdata.
  const viewerIsSuperAdmin = ["superadmin", "owner", "api_admin"].includes(adminUser?.role);
  const viewerIsAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(adminUser?.role);
  // Opf. #76: kan_redigere_elever giver fuld adgang til elev-stamkortet (alle sektioner,
  // alle faner, alle felter). kan_se_elever giver samme scope, kun read-only.
  const viewerKanRedigereElever = !viewerIsAdmin && !!adminUser?.rettigheder?.kan_redigere_elever;
  const viewerKanSeElever = !viewerIsAdmin && !!adminUser?.rettigheder?.kan_se_elever;
  // Fuld adgang som isAdmin HVIS kan_redigere_elever — delegeret elev-håndtering.
  const elevAdgangSomAdmin = viewerIsAdmin || viewerKanRedigereElever;

  const VALID_PERIODS = ["day", "week", "month", "last_month", "prev_month"];
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("itab") || "student";
  const periodFromUrl = searchParams.get("iperiod");
  const activeView = ["student", "timer", "maal", "okonomi", "karakterer", "hub"].includes(tabFromUrl) ? tabFromUrl : "student";
  const studentPeriod = VALID_PERIODS.includes(periodFromUrl) ? periodFromUrl : "week";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => next.set(k, v));
      return next;
    }, { replace: true });
  };

  const [studentData, setStudentData] = useState(null);
  const [timerData, setTimerData] = useState(null);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerError, setTimerError] = useState(null);
  const [timerProgress, setTimerProgress] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/student?period=${studentPeriod}`, { headers: authHeaders })
      .then(r => setStudentData(r.data))
      .catch(() => {});
  }, [studentPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTimerData = async () => {
    setTimerLoading(true);
    setTimerError(null);
    setTimerProgress(null);
    const pollInterval = setInterval(async () => {
      try {
        const p = await axios.get(`${API}/dashboard/timer/progress`, { headers: authHeaders });
        if (p.data.active) setTimerProgress(p.data);
      } catch {}
    }, 1500);
    try {
      const res = await axios.get(`${API}/dashboard/timer`, { headers: authHeaders, timeout: 180000 });
      setTimerData(res.data);
    } catch (err) {
      setTimerError(err.response?.data?.detail || err.message || "Ukendt fejl");
    } finally {
      clearInterval(pollInterval);
      setTimerLoading(false);
      setTimerProgress(null);
    }
  };

  useEffect(() => {
    if (activeView === "timer" && !timerData && !timerLoading) fetchTimerData();
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  const PERIOD_OPTIONS = [
    { key: "day", label: t(lang, "periodDay") },
    { key: "week", label: t(lang, "periodWeek") },
    { key: "month", label: t(lang, "periodMonth") },
    { key: "last_month", label: t(lang, "periodLastMonth") },
    { key: "prev_month", label: t(lang, "periodPrevMonth") },
  ];

  // Decode city from impToken
  let studentCity = null;
  try {
    const payload = JSON.parse(atob(impToken.split(".")[1]));
    studentCity = payload.city || null;
  } catch {}

  const displayCity = (city) => city === "Koebenhavn" ? "København" : city;

  return (
    <div className="min-h-screen bg-background">
      {/* Header with back button */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="w-full flex justify-center py-2 bg-[#FF2A6D]/10 border-b border-[#FF2A6D]/20">
          <button
            onClick={onExit}
            className="flex items-center gap-2 px-5 py-1.5 rounded-full bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02460] transition-colors"
          >
            ← Tilbage til min profil
          </button>
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div>
            <span className="text-base font-bold text-foreground">{studentName}</span>
            {studentCity && <span className="text-[13px] text-muted-foreground ml-2">{displayCity(studentCity)}</span>}
          </div>
          <span className="text-[13px] text-muted-foreground italic">Vises af {adminUser?.name}</span>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <Tabs value={activeView} onValueChange={(v) => updateParams({ itab: v })} className="w-full">
          <div className="mt-4 mb-6 flex items-center justify-between flex-wrap gap-3">
            <TabsList className="h-11 sm:h-12 bg-secondary border border-border p-1 rounded-full w-full sm:w-auto overflow-x-auto justify-start sm:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsTrigger value="student" className="rounded-full px-3 sm:px-6 py-2 sm:py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap shrink-0 data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out">Score</TabsTrigger>
              <TabsTrigger value="timer" className="rounded-full px-3 sm:px-6 py-2 sm:py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap shrink-0 data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out">Timer</TabsTrigger>
              <TabsTrigger value="maal" className="rounded-full px-3 sm:px-6 py-2 sm:py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap shrink-0 data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out">Mål</TabsTrigger>
              <TabsTrigger value="okonomi" className="rounded-full px-3 sm:px-6 py-2 sm:py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap shrink-0 data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out">Stamdata</TabsTrigger>
              <TabsTrigger value="karakterer" className="rounded-full px-3 sm:px-6 py-2 sm:py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap shrink-0 data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out">Karakterer</TabsTrigger>
              {(viewerIsAdmin || viewerKanSeElever || viewerKanRedigereElever) && (
                <TabsTrigger value="hub" className="rounded-full px-3 sm:px-6 py-2 sm:py-2.5 text-[13px] sm:text-sm font-semibold whitespace-nowrap shrink-0 data-[state=active]:bg-[#FF2A6D] data-[state=active]:text-white data-[state=active]:shadow-lg transition-transform duration-200 ease-out">Hub</TabsTrigger>
              )}
            </TabsList>
            {activeView === "student" && (
              <SubTabs
                tabs={PERIOD_OPTIONS}
                value={studentPeriod}
                onChange={(key) => updateParams({ iperiod: key })}
                className="w-full sm:w-auto"
              />
            )}
          </div>

          <TabsContent value="student" className="mt-0">
            {studentData
              ? <StudentView data={studentData} period={studentPeriod} refreshing={false} lang={lang} />
              : <ScoreboardLoading />}
          </TabsContent>
          <TabsContent value="timer" className="mt-0">
            {timerData ? (
              <TimerView data={timerData} userCity={studentCity} isAdmin={false} isGuest={true} />
            ) : timerLoading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">{timerProgress?.phase || "Henter timerdata..."}</p>
                {timerProgress?.pct != null && (
                  <div className="w-48 h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF2A6D] transition-all" style={{ width: `${timerProgress.pct}%` }} />
                  </div>
                )}
              </div>
            ) : timerError ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <p className="text-sm text-red-400">Fejl: {timerError}</p>
                <button onClick={fetchTimerData} className="px-4 py-2 rounded-full text-[13px] font-semibold bg-secondary text-foreground hover:bg-secondary/80">Prøv igen</button>
              </div>
            ) : null}
          </TabsContent>
          <TabsContent value="maal" className="mt-0">
            <StudentGoalView token={impToken} />
          </TabsContent>
          <TabsContent value="okonomi" className="mt-0">
            {/* Opf. #76: elevAdgangSomAdmin = viewerIsAdmin ELLER kan_redigere_elever.
                Begge får fuld redigeringsadgang til alle sektioner på Stamdata-fanen.
                isSuperAdmin styrer fortrolige tabs (Kontrakter, Betaling, Noter) — kun superadmin/owner. */}
            <ElevOkonomiView
              token={impToken}
              adminToken={adminToken}
              isAdmin={elevAdgangSomAdmin}
              isSuperAdmin={viewerIsSuperAdmin}
              canEditUgetabel={elevAdgangSomAdmin}
            />
          </TabsContent>
          <TabsContent value="karakterer" className="mt-0">
            <KaraktererTab
              token={impToken}
              adminToken={adminToken}
              elevNavn={studentName}
            />
          </TabsContent>
          {(viewerIsAdmin || viewerKanSeElever || viewerKanRedigereElever) && (
            <TabsContent value="hub" className="mt-0">
              <ElevHubView
                token={impToken}
                currentUser={adminUser?.name}
                userCity={studentCity}
                isAdmin={elevAdgangSomAdmin}
                isSuperAdmin={viewerIsSuperAdmin}
                isMember={false}
                activeTab="student"
                onTabChange={() => {}}
              />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
